import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Scale, Users, UserPlus, Trash2, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/team"); setMembers(data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMember = async () => {
    if (!form.email.trim() || !form.password) {
      toast.error("Email and temporary password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/team", { name: form.name.trim() || "Member", email: form.email.trim(), password: form.password });
      toast.success("Team member created");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/team/${deleteTarget.id}`);
      toast.success("Member removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-16 text-slate-100">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0F17]/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600/15 ring-1 ring-blue-500/30">
              <Scale className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-slate-50">Team Management</h1>
              <p className="font-mono-data text-[11px] text-slate-500">Members with dashboard access</p>
            </div>
          </div>
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-5 pt-6">
        <div className="overflow-hidden rounded-lg border border-white/10 bg-[#111723]">
          <div className="flex items-center justify-between border-b border-white/10 p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-slate-400" />
              <span className="font-display text-lg font-bold text-slate-50">Members</span>
              <span className="ml-1 rounded-full bg-slate-800 px-2 py-0.5 font-mono-data text-xs text-slate-400">{members.length}</span>
            </div>
            <Button data-testid="add-member-button" onClick={() => setOpen(true)} className="h-9 bg-blue-600 text-white hover:bg-blue-700">
              <UserPlus className="mr-1.5 h-4 w-4" /> Add Member
            </Button>
          </div>

          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Added</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-testid="team-table-body">
              {members.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`} className="transition-colors duration-200 hover:bg-[#1A2234]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-200">{m.name}</span>
                      {m.id === user?.id && (
                        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] uppercase text-emerald-400">You</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-300">{m.email}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(m.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`delete-member-${m.id}`}
                      onClick={() => setDeleteTarget(m)}
                      disabled={m.id === user?.id}
                      className="rounded p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-30"
                      title={m.id === user?.id ? "You cannot remove yourself" : "Remove member"}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-lg border border-white/10 bg-[#111723] p-4 text-xs text-slate-400">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
          <p>Every member you add gets full dashboard access. Share the temporary password securely — they can sign in immediately at the login page.</p>
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#111723] text-slate-100 sm:max-w-md" data-testid="add-member-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add Team Member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create an account directly. The member signs in with this email and temporary password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-400">Full Name</Label>
              <Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Advocate" className="mt-1 border-white/10 bg-[#0B0F17] text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Email</Label>
              <Input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@firm.com" className="mt-1 border-white/10 bg-[#0B0F17] text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Temporary Password</Label>
              <Input data-testid="member-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 characters" className="mt-1 border-white/10 bg-[#0B0F17] text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400 hover:bg-slate-800 hover:text-white">Cancel</Button>
            <Button data-testid="save-member-button" onClick={addMember} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#111723] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove member?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              <span className="font-mono-data text-blue-400">{deleteTarget?.email}</span> will lose access to the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-member" onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
